import { User, AvailableInterviewersResponse } from "../Entities/User";
import { FindAllCandidatesFilter } from "../Interface";
import { GetAllMembersWithInterviewAvailabilityDto } from "../Dto/TeamMember.dto";
import apiClient from "./api.client.service";

class UserService {
  private users: User[] = [];
  private APIBaseUrl: string;

  constructor() {
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
  }

  async loadData() {
    try {
      const response = await apiClient.get(`${this.APIBaseUrl}/users`);
      const data = response.data;
      this.users = data;
      return this.users;
    } catch (error) {
      console.error("🚀 ~ UserService ~ loadData ~ error:", error);
    }
  }

  async getAllUsers() {
    try {
      if (!this.users || !this.users?.length) {
        await this.loadData();
      }

      return this.users;
    } catch (error) {
      console.error("🚀 ~ UserService ~ getAllUsers ~ error:", error);
      return [];
    }
  }

  async getUserById(userId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/users/${userId}`,
      );
      return response.data;
    } catch (error) {
      console.error("🚀 ~ UserService ~ getUserById ~ error:", error);
    }
  }

  async authenticateUser(email: string, password: string) {
    try {
      const admin: User = {
        _id: "1",
        email: "admin@gmail.com",
        firstName: "admin",
        lastName: "jobzy",
        isActive: true,
        password: "admin123",
        orgId: "1",
        role: "admin",
        createdAt: new Date(),
        updatedAt: new Date(),
        isEmailVerified: true,
      };
      if (email === admin.email && password === admin.password) {
        return { success: true, user: admin };
      }
      return { success: false, message: "Invalid email or password" };
    } catch (error) {
      console.error("🚀 ~ UserService ~ authenticateUser ~ error:", error);
      return {
        success: false,
        message: "An error occurred during authentication",
      };
    }
  }

  async getUserByFilter(searchQuery: string): Promise<User[]> {
    try {
      let email = "";
      let name = "";

      if (searchQuery.includes("@")) {
        email = searchQuery;
      } else {
        name = searchQuery;
      }
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/users/search?email=${email}&name=${name}`,
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async getUserProfileByProfileId(profileId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/users/profile/${profileId}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ UserService ~ getUserProfileByProfileId ~ error:",
        error,
      );
    }
  }

  async getAllUsersByRole(role: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/users/roles/${role}`,
      );
      return response.data;
    } catch (error) {
      console.error("🚀 ~ UserService ~ getAllUsersByRole ~ error:", error);
    }
  }

  async blockOrUnblockUser(userId: string, action: "block" | "unblock") {
    try {
      const response = await apiClient.patch(
        `${this.APIBaseUrl}/admin/users/${userId}/${action}`,
      );
      return response.data;
    } catch (error) {
      console.error("🚀 ~ UserService ~ blockOrUnblockUser ~ error:", error);
    }
  }

  async getAvailableInterviewers(
    page: number = 1,
    limit: number = 10,
  ): Promise<AvailableInterviewersResponse> {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/users/available-interviewers?page=${page}&limit=${limit}`,
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch available interviewers:", error);
      throw new Error("Failed to fetch available interviewers");
    }
  }

  async getAllUsersWithProfile(
    filters: FindAllCandidatesFilter,
    page: number,
    limit: number,
  ) {
    const { normalSearch, querySearch, sort, source } = filters;
    const params: Record<string, any> = {};
    if (page && page > 0) params.page = page;
    if (limit && limit > 0) {
      params.limit = limit;
    } else {
      params.limit = 10;
    }
    if (normalSearch) params.normalSearch = normalSearch;
    if (querySearch) params.querySearch = querySearch;
    if (sort) params.sort = sort;
    if (source) params.source = source;

    const queryString = Object.keys(params)
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`,
      )
      .join("&");
    try {
      const url = `${this.APIBaseUrl}/admin/users/profile?${queryString}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ UserService ~ getAllUsersWithProfile ~ error:",
        error,
      );
    }
  }

  async getAllMembers(page: number, limit: number, search: string | null) {
    try {
      let url = `${this.APIBaseUrl}/admin/users/members/interview-availability?page=${page}&limit=${limit}`;
      if (search) {
        url += `&search=${search}`;
      }
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error("🚀 ~ UserService ~ getAllMembers ~ error:", error);
    }
  }

  async getAllMembersWithFilters(
    filters: GetAllMembersWithInterviewAvailabilityDto,
  ) {
    try {
      const { page, limit, search, isActive, role, sort } = filters;

      const params: Record<string, any> = {
        page,
        limit,
      };

      if (search) params.search = search;
      if (isActive !== null) params.isActive = isActive;
      if (role) params.role = role;
      if (sort) params.sort = sort;

      const queryString = Object.keys(params)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`,
        )
        .join("&");

      const url = `${this.APIBaseUrl}/admin/users/members/interview-availability?${queryString}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ UserService ~ getAllMembersWithFilters ~ error:",
        error,
      );
      throw error;
    }
  }

  async getAllMembersStatistics() {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/users/members/statistics`,
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "🚀 ~ UserService ~ getAllMembersStatistics ~ error:",
        error,
      );
    }
  }

  async getCandidateInfo(candidateId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/users/candidate/${candidateId}`,
      );
      return response.data;
    } catch (error: any) {
      console.error("~ UserService ~ getCandidateInfo ~ error:", error);
      return null;
    }
  }
}

const userService = new UserService();
export default userService;
